import { useAuth } from '@clerk/nextjs';
import { queryOptions, useQuery } from '@tanstack/react-query';

import { QueryVariables } from 'queries/types';
import { TICKETS_PREFIX } from 'restful/tickets/constants';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type { GetGeneratingTicketsParams } from 'services/api/restful/generated/nofomoRestApi.schemas';
import { getGeneratingTickets } from 'services/api/restful/generated/tickets/tickets';

type GeneratingTicketsQueryData = { generatingTickets: boolean };

const toGeneratingTicketsParams = (
  variables: GetGeneratingTicketsParams,
): GetGeneratingTicketsParams | undefined => {
  const params: GetGeneratingTicketsParams = {};

  if (variables.eventId) params.eventId = variables.eventId;
  if (variables.timeslotId) params.timeslotId = variables.timeslotId;

  return Object.keys(params).length > 0 ? params : undefined;
};

const accountTicketsGeneratingQuery = (
  variables: GetGeneratingTicketsParams = {},
) =>
  queryOptions<GeneratingTicketsQueryData, RestApiError>({
    queryKey: [TICKETS_PREFIX, 'generating', variables],
    queryFn: async () => {
      const { data } = await restCall(() =>
        getGeneratingTickets(toGeneratingTicketsParams(variables)),
      );

      return { generatingTickets: Boolean(data) };
    },
  });

export const useAccountGeneratingTickets = (
  variables: QueryVariables<typeof accountTicketsGeneratingQuery> = {},
  config: Partial<ReturnType<typeof accountTicketsGeneratingQuery>> = {},
) => {
  const { isSignedIn } = useAuth();

  const query = useQuery({
    ...accountTicketsGeneratingQuery(variables),
    retry: false,
    ...config,
    enabled: !!isSignedIn && config.enabled,
  });

  return {
    generatingTickets: !!query.data?.generatingTickets,
    status: query.status,
  };
};
